import type { ReactElement, ReactNode } from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AuthActions } from 'common/auth/actions';
import { BrowserTabFlowGate } from 'common/component/BrowserTabFlowGate';

import {
    selectHandoffActiveStudySite,
    selectIsRespondentOnHandedOffDevice,
    selectRespondentActivityLocation,
} from '../state/selectors';

import { DeviceHandoffActiveInAnotherTabScreen } from './DeviceHandoffActiveInAnotherTabScreen';

interface Props {
    children: ReactNode;
}

export const DeviceHandoffOwnerTabGate = ({ children }: Props): ReactElement => {
    const isRespondentOnHandedOffDevice = useSelector(selectIsRespondentOnHandedOffDevice);
    const respondentActivityLocation = useSelector(selectRespondentActivityLocation);
    const handoffActiveStudySite = useSelector(selectHandoffActiveStudySite);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const resumeInThisTab = useCallback(
        (takeOverFlowInThisTab: () => void) => {
            takeOverFlowInThisTab();

            if (handoffActiveStudySite) {
                dispatch(
                    AuthActions.updateActiveStudySite({
                        study: handoffActiveStudySite.activeStudy,
                        site: handoffActiveStudySite.activeSite,
                    })
                );
            }

            if (respondentActivityLocation) {
                navigate(respondentActivityLocation, { replace: true });
            }
        },
        [dispatch, handoffActiveStudySite, navigate, respondentActivityLocation]
    );

    return (
        <BrowserTabFlowGate
            flowName="deviceHandoff"
            confineToOwnerTab={isRespondentOnHandedOffDevice}
            renderFallback={({ takeOverFlowInThisTab }) => (
                <DeviceHandoffActiveInAnotherTabScreen onResume={() => resumeInThisTab(takeOverFlowInThisTab)} />
            )}
        >
            {children}
        </BrowserTabFlowGate>
    );
};
